import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { UserApiService } from '../../../core/services/user-api.service';
import { CustomDatePipe } from '../../../core/pipes/custom-date.pipe';
import { Router } from '@angular/router';
import { BuyerServiceService } from '../../../core/services/buyers/buyer-service.service';
import { CompanyCodeService } from '../../../core/services/adminService/company-code.service';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzTableModule } from 'ng-zorro-antd/table';
import { IOtherBuyerRequest } from '../../../core/interfaces/buyer';
import { constantTxt } from '../../../core/constants/constants';
import { Subscription } from 'rxjs';

@Component({
  selector: 'buyer-other-request',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    CustomDatePipe,
    NzButtonModule,
    NzSelectModule,
    NzInputModule,
    NzTableModule,
  ],
  templateUrl: './buyer-other-manage-request-form.component.html',
  styleUrls: ['./buyer-other-manage-request-form.component.less'],
})
export class BuyerOtherManageRequestFormComponent implements OnInit, OnDestroy {
  manageForm!: FormGroup;
  requests: IOtherBuyerRequest[] = [];
  companies: any[] = [];
  buyers: any[] = [];
  purchasingOffices: any[] = [];
  statuses = [
    'ALL',
    'DRAFT',
    'OPEN',
    'IN PROGRESS',
    'PARTIAL QUOTED',
    'CLOSED',
    'CANCELED',
  ];
  noDataAvailable = false;
  pageNumber = 1;
  pageSize = 100;
  total = 0;
  attachmentMap: { [key: string]: boolean } = {};
  isSearchMode = false;
  customNoDataMessage = constantTxt.noDataFound;
  companiesSubscription!: Subscription;
  getBuyersSubscription!: Subscription;
  getOfficesListSubscription!: Subscription;
  otherBuyerPnaSearchSubscription!: Subscription;

  constructor(
    private readonly fb: FormBuilder,
    private readonly userApi: UserApiService,
    private readonly BuyerServiceService: BuyerServiceService,
    private readonly router: Router,
    private readonly companyCodeService: CompanyCodeService
  ) {}

  ngOnInit(): void {
    this.initializeForm();
    this.loadInitialData();
    this.onSearch();
  }

  initializeForm() {
    this.manageForm = this.fb.group({
      companyCode: [''],
      buyer: [''],
      purchasingOffice: [''],
      status: ['IN PROGRESS'],
      pnaNo: ['', [Validators.pattern('^\\d+(?:-\\d+)?$')]],
    });
  }

  loadInitialData(): void {
    this.companiesSubscription = this.companyCodeService
      .getCompanies()
      .subscribe({
        next: (res) => {
          let data = res.body;
          if (data) {
            this.companies = data;
          }
        },
        error: (error) => {
          console.log('Error occured in fetching companies. Error :', error);
        },
      });

    this.getBuyersSubscription = this.userApi.getBuyers().subscribe({
      next: (res) => {
        let data = res.body;
        if (data) {
          this.buyers = data;
        }
      },
      error: (error) => {
        console.log('Error occured in fetching buyers. Error :', error);
      },
    });

    this.getOfficesListSubscription = this.userApi.getOfficesList().subscribe({
      next: (res) => {
        let data = res.body;
        if (data) {
          this.purchasingOffices = data;
        }
      },
      error: (error) => {
        console.log('Error occured in fetching offices. Error :', error);
      },
    });
  }

  onSearch() {
    this.isSearchMode = true;
    const formValue = this.manageForm.value;
    const filter = {
      Ccn: formValue.companyCode ? Number(formValue.companyCode) : null,
      Buyer: formValue.buyer || null,
      Purchasing: formValue.purchasingOffice || null,
      Status: formValue.status || null,
      PnaNo: formValue.pnaNo || null,
      TxtFilter: formValue.txtFilter || null,
    };
    this.otherBuyerPnaSearchSubscription =
      this.BuyerServiceService.otherBuyerPnaSearch(
        filter,
        this.pageNumber,
        this.pageSize
      ).subscribe({
        next: (res) => {
          let data = res.body;
          if (data) {
            this.requests = data.pnaRequests;
            this.total = data.metadata?.totalCount || 1000;
          } else {
            this.requests = [];
          }
        },
        error: () => {
          this.requests = [];
        },
      });
  }

  onPageSizeChange(pageSize: number): void {
    this.pageSize = pageSize;
    this.pageNumber = 1;
    this.onSearch();
  }

  onPageIndexChange(pageIndex: number): void {
    this.pageNumber = pageIndex;
    this.onSearch();
  }

  navigateToView(pnaNo: string, pnaYear: string): void {
    const navigationState = {
      mode: 'otherview',
      pnaNo: pnaNo,
      year: pnaYear,
      role: 'lead-buyer',
      returnUrl: '/buyer/other-request',
    };

    this.router.navigate(['/buyer/view-other-request'], {
      state: navigationState,
    });
  }

  ngOnDestroy(): void {
    if (this.companiesSubscription) {
      this.companiesSubscription.unsubscribe();
    }
    if (this.getBuyersSubscription) {
      this.getBuyersSubscription.unsubscribe();
    }
    if (this.getOfficesListSubscription) {
      this.getOfficesListSubscription.unsubscribe();
    }
    if (this.otherBuyerPnaSearchSubscription) {
      this.otherBuyerPnaSearchSubscription.unsubscribe();
    }
  }
}
